import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/student/PageHeader";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import logo_icon from "@/assets/images/logos/ofcourse-bar-logo.svg";
import {
  apiStartModuleTest,
  apiGetModuleTestSessionExercises,
  apiSubmitModuleTestAnswers,
  apiFinishModuleTest,
  type ModuleTestSessionExerciseDto,
} from "@/services/student/module-test.api";
import { getModuleInfo } from "@/services/student/module.service";
import type { ModuleInfo } from "@/services/student/module.interfaces";
import { ExerciseItem } from "@/services/student/exercise.interfaces";
import { evaluateExerciseAnswerOnly } from "@/services/student/exercise.service";
import { MultipleChoices } from "@/pages/Student/Exercises/ExerciseTypes/MultipleChoices";
import { Loader2, AlertCircle } from "lucide-react";

/** Converte DTO da API para ExerciseItem usado pelo MultipleChoices. */
function mapSessionExerciseToItem(
  dto: ModuleTestSessionExerciseDto,
  index: number
): ExerciseItem {
  let content: { question?: string; options?: Array<{ id: string; text: string; order: number }> } = {};
  try {
    content = typeof dto.data === "string" ? JSON.parse(dto.data) : dto.data;
  } catch {
    content = {};
  }
  let answerData: { multipleResponses?: boolean; correctOptionCount?: number } = {};
  try {
    if (dto.answerData)
      answerData = typeof dto.answerData === "string" ? JSON.parse(dto.answerData) : dto.answerData;
  } catch {
    answerData = {};
  }
    const options = (content.options ?? []).map((o: { id?: string; text: string; order?: number }, i: number) => ({
      id: o.id ?? String(i + 1),
      text: o.text,
      order: o.order ?? i,
    }));
    return {
    id: dto.id,
    type: (dto.type ?? "multiple_choice").toLowerCase(),
    title: `Questão ${index + 1}`,
    content: {
      question: content.question,
      multipleCorrectAnswers: answerData.multipleResponses === true,
      options,
    },
    answerData: {
      multipleResponses: answerData.multipleResponses,
      correctOptionCount: answerData.correctOptionCount,
    },
    moduleId: dto.moduleId,
    createdAt: new Date(),
  };
}

/**
 * Página do teste do módulo: start, respostas e finalizar.
 * Usa o componente comum MultipleChoices (spec: use-common-multiple-choice-on-module-test).
 */
export const ModuleTestPage = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = location.state as
    | { sessionId?: string; exerciseIds?: string[] }
    | null;
  const [loading, setLoading] = useState(!initialState?.sessionId);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(
    initialState?.sessionId ?? null
  );
  const [exerciseIds, setExerciseIds] = useState<string[]>(
    initialState?.exerciseIds ?? []
  );
  /** Exercícios da sessão (ExerciseItem) para exibir com MultipleChoices. */
  const [exerciseItems, setExerciseItems] = useState<ExerciseItem[]>([]);
  /** Indica que a lista de exercícios já foi carregada (permite distinguir "vazio" de "carregando"). */
  const [exercisesFetched, setExercisesFetched] = useState(false);
  /** Respostas por exerciseId (valor string[] para múltipla escolha, compatível com submit). */
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  /** True enquanto envia respostas e finaliza o teste (após verificar o último exercício). */
  const [finalizing, setFinalizing] = useState(false);
  /** Nome/título do módulo para o subtítulo (evita exibir GUID). */
  const [moduleInfo, setModuleInfo] = useState<ModuleInfo | null>(null);
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, []);

  // Buscar nome/título do módulo para o subtítulo (assessment: não exibir GUID)
  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    getModuleInfo(moduleId)
      .then((info) => {
        if (!cancelled) setModuleInfo(info);
      })
      .catch(() => {
        if (!cancelled) setModuleInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Start test when no session in state
  useEffect(() => {
    if (!moduleId) {
      setError("Módulo não informado.");
      setLoading(false);
      return;
    }
    if (initialState?.sessionId && initialState?.exerciseIds) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiStartModuleTest(moduleId)
      .then((data) => {
        if (!cancelled) {
          setSessionId(data.sessionId);
          setExerciseIds(data.exerciseIds ?? []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const data = err?.response?.data;
          const message =
            err?.response?.status === 404
              ? "Serviço de teste do módulo ainda não disponível. Tente mais tarde."
              : typeof data === "string"
                ? data
                : (data?.message ?? "Não foi possível iniciar o teste. Tente novamente.");
          setError(message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Fetch session exercises when we have sessionId (after start or from nav state)
  useEffect(() => {
    if (!sessionId || exerciseItems.length > 0) return;
    let cancelled = false;
    setExercisesFetched(false);
    apiGetModuleTestSessionExercises(sessionId)
      .then((list) => {
        if (!cancelled) {
          setExerciseItems(list.map((dto, i) => mapSessionExerciseToItem(dto, i)));
          setExercisesFetched(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Não foi possível carregar as questões.");
          setExercisesFetched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  /** Envia respostas ao backend e finaliza o teste; usado ao verificar o último exercício. */
  const submitAndFinish = async (
    answersToSubmit: Record<string, unknown>
  ) => {
    if (!sessionId || !moduleId) return;
    const items = Object.entries(answersToSubmit).filter(
      ([_, v]) => v !== undefined && v !== "" && (Array.isArray(v) ? v.length > 0 : true)
    );
    if (items.length === 0) return;
    setFinalizing(true);
    try {
      await apiSubmitModuleTestAnswers(
        sessionId,
        items.map(([exerciseId, answer]) => ({ exerciseId, answer }))
      );
      const result = await apiFinishModuleTest(sessionId);
      navigate(`/student/modules/${moduleId}/test/result`, {
        state: {
          scorePercent: result.scorePercent,
          passed: result.passed,
          unlockedModules: result.unlockedModules ?? [],
        },
      });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      setError(
        e?.response?.data?.message ||
          "Não foi possível finalizar o teste. Tente novamente."
      );
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="w-full">
      <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          avatarAlt="Logo OfCourse"
          title="Teste do módulo"
          subtitle={moduleInfo?.name ?? moduleInfo?.title ?? (moduleId ? `Módulo ${moduleId}` : "Módulo")}
          avatar={logo_icon}
          showActions={true}
        />
      </div>

      <div className="w-full bg-gray-50 p-6 mt-6">
        <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600" aria-hidden />
            <p className="text-base text-gray-800">Iniciando teste...</p>
          </div>
        )}

        {error && !loading && (
          <div
            role="alert"
            className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900"
          >
            <AlertCircle className="w-5 h-5 shrink-0" aria-hidden />
            <p className="text-base">{error}</p>
          </div>
        )}

        {!loading && !error && sessionId && (
          <div className="space-y-6">
            <div className="m-2 sm:m-4">
              <p className="text-base text-gray-800 mb-4">
                {exerciseItems.length > 0 ? (
                  <>
                    <span className="text-sm text-gray-600 mr-1">{exerciseItems.length} questões.</span>
                    Responda e verifique cada questão. O teste termina ao verificar a última.
                  </>
                ) : exercisesFetched ? (
                  "O pool de questões deste módulo está vazio. Adicione questões ao pool do teste no painel admin."
                ) : (
                  "Carregando questões..."
                )}
              </p>
            </div>
            {exerciseItems.map((item, index) => (
              <div
                key={item.id}
                className="rounded-lg"
              >
                <MultipleChoices
                  item={item}
                  moduleId={moduleId ?? undefined}
                  onAnswer={async (selectedOptions) => {
                    setAnswers((prev) => ({ ...prev, [item.id]: selectedOptions }));
                    const result = await evaluateExerciseAnswerOnly({
                      exerciseId: item.id,
                      moduleId: moduleId ?? item.moduleId ?? "",
                      answer: selectedOptions,
                      exerciseType: "multiple_choice",
                    });
                    const merged = { ...answersRef.current, [item.id]: selectedOptions };
                    const isLastExercise = index === exerciseItems.length - 1;
                    const allAnswered = exerciseItems.every((ex) => ex.id in merged);
                    if (isLastExercise && allAnswered) {
                      await submitAndFinish(merged);
                    }
                    return {
                      isCorrect: result.isCorrect,
                      feedback: result.feedback,
                      expectedAnswer: result.expectedAnswer,
                    };
                  }}
                />
              </div>
            ))}
            {finalizing && (
              <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-800" role="status" aria-live="polite">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" aria-hidden />
                <p className="text-base">Finalizando teste...</p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
